import React, { useState, useEffect } from 'react';
import { useGetSevaCategoriesQuery, useGetSevasByCategoryQuery } from '../redux/api/sevaApi';
import { useNavigate } from 'react-router-dom';
import './OnlineSevaBooking.css';

/* ─── Individual Seva Card ─────────────────────────────────────── */
const SevaCard = ({ seva, categoryName, isSelected, onToggle }) => {
  const BASE_URL = "";
  return (
    <div
      className={`seva-card ${isSelected ? 'selected-card' : ''}`}
      onClick={() => onToggle(seva, categoryName)}
    >
      {/* Checkmark badge (visible when selected) */}
      <span className="check-icon">✓</span>

      {/* Optional small icon image ON the card itself */}
      {seva.image && (
        <div className="seva-card-image-container">
          <img
            src={`${BASE_URL}${seva.image}`}
            alt={seva.seva_name}
            className="seva-card-image"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <h5 className="seva-title">{seva.seva_name}</h5>
      <div className="seva-price">Rs. {seva.seva_amount}/-</div>
    </div>
  );
};

/* ─── Category Section ─────────────────────────────────────────── */
const SevaCategorySection = ({ category, cartItems, onToggleSeva }) => {
  const { data: sevasData, isLoading, isError } = useGetSevasByCategoryQuery(category._id);

  if (isLoading) return (
    <div className="loading-container">Loading {category.category_name}…</div>
  );
  if (isError) return null;
  if (!sevasData || !sevasData.data || sevasData.data.length === 0) return null;

  const imageSrc = category.image_path || null;

  return (
    <div className="category-section mb-5">
      {/* ── Coloured header strip ── */}
      <div className="category-header-strip">
        <h3 className="category-title">{category.category_name}</h3>
      </div>

      {/* ── Body: sidebar + grid ── */}
      <div className="category-body">
        {/* Left Sidebar */}
        <div className="category-sidebar">
          {imageSrc ? (
            <div className="category-image-wrapper">
              <img
                src={imageSrc}
                alt={category.category_name}
                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
              />
            </div>
          ) : (
            /* Decorative placeholder when no image */
            <div
              className="category-image-wrapper"
              style={{
                background: 'linear-gradient(135deg, #f5e6e0 0%, #ffe0d0 100%)',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '4rem',
                aspect: '1',
              }}
            >
              🛕
            </div>
          )}

          {category.description && (
            <p className="category-desc">{category.description}</p>
          )}
          {category.meaning && (
            <p className="category-desc">
              <strong>Meaning:</strong> {category.meaning}
            </p>
          )}
        </div>

        {/* Right: Seva Cards Grid */}
        <div className="seva-cards-area">
          {sevasData.data.map((seva) => {
            const isSelected = cartItems.some(item => item._id === seva._id);
            return (
              <SevaCard
                key={seva._id}
                seva={seva}
                categoryName={category.category_name}
                isSelected={isSelected}
                onToggle={onToggleSeva}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Page ────────────────────────────────────────────────── */
const OnlineSevaBooking = () => {
  const { data: categoriesData, isLoading, isError } = useGetSevaCategoriesQuery();
  const navigate = useNavigate();

  const [selectedDate, setSelectedDate] = useState(() => {
    return sessionStorage.getItem('selected_seva_date') || new Date().toISOString().split('T')[0];
  });

  useEffect(() => {
    sessionStorage.setItem('selected_seva_date', selectedDate);
  }, [selectedDate]);

  const todayStr = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setMonth(maxDate.getMonth() + 3);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  const [cartItems, setCartItems] = useState(() => {
    try {
      return JSON.parse(sessionStorage.getItem('cart_online')) || [];
    } catch {
      return [];
    }
  });

  useEffect(() => {
    sessionStorage.setItem('cart_online', JSON.stringify(cartItems));
  }, [cartItems]);

  const handleToggleSeva = (seva, categoryName) => {
    setCartItems((prev) => {
      const exists = prev.some((item) => item._id === seva._id);
      return exists
        ? prev.filter((item) => item._id !== seva._id)
        : [...prev, { ...seva, god_name: categoryName }];
    });
  };

  const totalAmount = cartItems.reduce((acc, item) => acc + Number(item.seva_amount), 0);

  return (
    <div className="eva-booking-page">
      {/* ─── Header ─── */}
      <div className="page-header-bar text-center">
        <div className="container">
          <h1 className="page-title">Online Seva Booking</h1>
          <p className="page-subtitle">Sri Adi Shankaracharya Sharada LakshmiNarasimha Peetam</p>
        </div>
      </div>

      <div className="container mt-4">
        {/* ─── Date Picker ─── */}
        <div className="row justify-content-center mb-5">
          <div className="col-lg-5 col-md-7">
            <div className="date-picker-card">
              <div className="date-card-label">
                <i className="fa-regular fa-calendar-days me-2"></i>Choose Seva Date
              </div>
              <input
                type="date"
                className="date-input"
                value={selectedDate}
                min={todayStr}
                max={maxDateStr}
                onChange={(e) => setSelectedDate(e.target.value)}
              />
              <p className="date-hint">Available from today up to 3 months ahead.</p>
            </div>
          </div>
        </div>

        {/* ─── Loading / Error ─── */}
        {isLoading && <div className="text-center p-5" style={{ color: '#999', fontFamily: 'Rajdhani, sans-serif', fontSize: '1.2rem' }}>Loading Categories…</div>}
        {isError   && <div className="text-center p-5 text-danger">Failed to load categories.</div>}

        {/* ─── Category Sections ─── */}
        {!isLoading && !isError && categoriesData?.data && (
          categoriesData.data
            .filter(cat => {
              if (cat.master_category_id &&
                (cat.master_category_id.name === "Guru Seva" ||
                 cat.master_category_id.name === "Donations")) {
                return false;
              }
              return true;
            })
            .map((category) => (
              <SevaCategorySection
                key={category._id}
                category={category}
                cartItems={cartItems}
                onToggleSeva={handleToggleSeva}
              />
            ))
        )}
      </div>

      {/* ─── Contact Note ─── */}
      <div className="container mb-5">
        <div className="contact-note-card">
          {/* Decorative circles */}
          <div className="contact-note-circle contact-note-circle--1" />
          <div className="contact-note-circle contact-note-circle--2" />

          {/* Icon */}
          <div className="contact-note-icon">📞</div>

          {/* Text */}
          <div className="contact-note-body">
            <div className="contact-note-eyebrow">Need More Sevas?</div>
            <p className="contact-note-text">
              For devotees wishing to avail sevas not listed above, please contact us at{' '}
              <a href="tel:+919019977466" className="contact-note-phone">
                +91 90199 77466
              </a>
            </p>
            <p className="contact-note-hours">
              🕘 9:00 AM – 6:00 PM &nbsp;|&nbsp; Monday to Saturday
            </p>
          </div>

          {/* CTA */}
          <a href="tel:+919019977466" className="contact-note-btn">
            Call Now
          </a>
        </div>
      </div>

      {/* ─── Fixed Bottom Footer ─── */}
      {cartItems.length > 0 && (
        <div className="fixed-bottom-footer">
          <div className="footer-content">
            <div className="footer-total">
              <span>Total:</span> ₹{totalAmount.toLocaleString('en-IN')}/-
            </div>
            <button
              className="btn-next"
              onClick={() => navigate('/seva-summary', { state: { cartItems, sevaDate: selectedDate, is_seva: true } })}
            >
              Next →
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OnlineSevaBooking;
